import { memo, useCallback, useState } from "react";
import Accordion from "../../../components/accordian";
import { useTranslation } from "react-i18next";
import {
  allowedFileTypes,
  ContentType,
  PERMIT_TO_WORK_ATTACHMENT_KEYS,
  PERMIT_TO_WORK_ATTACHMENTS_TYPE,
  PERMIT_TO_WORK_CATEGORIES,
  PermitToWorkDetails,
} from "../../../constants/common";
import FileUploadInput from "../../../components/inputs/FileUploadInput";
import toast from "react-hot-toast";
import ViewAttachmentModal from "../../../components/modal/ViewAttachmentModal";
import { useGetPermitToWorkFileByType } from "../../../hooks/permit/mutation";

interface AttachmentsProps {
  control: any;
  errors: any;

  isAttachments: {
    haveWorkPlan: number;
    haveMethodStatement: number;
    haveRiskAssessment: number;
  };
  uploadRefs: Record<string, React.MutableRefObject<HTMLInputElement | null>>;
  fileNames: Record<string, string>;
  setFileName: (key: string, value: React.SetStateAction<string>) => void;

  isFieldsDisabled?: boolean;

  selectedWorkCategory: string;

  permitDetails?: PermitToWorkDetails;
}

const Attachments = ({
  control,
  errors,
  isAttachments,
  uploadRefs,
  fileNames,
  setFileName,
  isFieldsDisabled,
  selectedWorkCategory,
  permitDetails,
}: AttachmentsProps) => {
  const { t } = useTranslation();

  const [selectedKey, setSelectedKey] = useState<string>("1");

  const [previewFiles, setPreviewFiles] = useState<
    Record<number, { url: string; contentType: string }>
  >({});
  const [imageSrc, setImageSrc] = useState("");
  const [contentType, setContentType] = useState("");
  const [isViewAttachmentModalOpen, setIsViewAttachmentModalOpen] =
    useState(false);
  const [selectedType, setSelectedType] = useState<number>(0);
  const [isGetFileByTypeLoading, setIsGetFileByTypeLoading] = useState(false);

  const {
    mutateAsync: GetPermitToWorkFileByType,
    isPending: isGetPermitToWorkFileByTypeLoading,
  } = useGetPermitToWorkFileByType();

  const handleAttachmentView = useCallback(
    async (type: number) => {
      try {
        // If we already have a local preview for this attachment type
        if (previewFiles && previewFiles[type]) {
          setImageSrc(previewFiles[type].url);
          setContentType(previewFiles[type].contentType);
          setIsViewAttachmentModalOpen(true);
          return;
        }

        if (permitDetails?.id) {
          setSelectedType(type);

          const body = {
            Id: permitDetails?.id,
            Type: type,
          };

          const res = await GetPermitToWorkFileByType(body);

          setIsGetFileByTypeLoading(isGetPermitToWorkFileByTypeLoading);

          if (res) {
            let url = "";
            if (res?.contentType !== ContentType.PDF) {
              url = `data:${res?.contentType};base64,${res?.base64}`;
            } else {
              const binaryString = atob(res?.base64);
              const byteArray = new Uint8Array(binaryString.length);

              for (let i = 0; i < binaryString.length; i++) {
                byteArray[i] = binaryString.charCodeAt(i);
              }

              const blob = new Blob([byteArray], { type: ContentType.PDF });
              url = URL.createObjectURL(blob);
            }
            setImageSrc(url);
            setContentType(res?.contentType);
            setIsViewAttachmentModalOpen(true);
          }
        }
      } catch (error) {
        toast.error("Something went wrong");
      } finally {
        setSelectedType(0);
      }
    },
    [
      GetPermitToWorkFileByType,
      isGetPermitToWorkFileByTypeLoading,
      permitDetails?.id,
      previewFiles,
    ],
  );

  return (
    <>
      <div className="bg-white z-10 p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
        <Accordion
          id="1"
          selectedKey={selectedKey}
          onSelectionChange={setSelectedKey}
          title={
            <h1 className="text-xl md:text-2xl flex flex-1 -mt-2 items-center">
              {t("4")}. {t("attachments")}
            </h1>
          }
        >
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
            <FileUploadInput
              name="uploadWorkPlan"
              label={t("uploadWorkPlan")}
              accept=".png,.jpg,.jpeg,.pdf"
              allowedTypes={allowedFileTypes}
              isAttachments={isAttachments?.haveWorkPlan}
              control={control}
              uploadRef={uploadRefs[PERMIT_TO_WORK_ATTACHMENT_KEYS.WORK_PLAN]}
              fileName={fileNames[PERMIT_TO_WORK_ATTACHMENT_KEYS.WORK_PLAN]}
              setFileName={(val) =>
                setFileName(PERMIT_TO_WORK_ATTACHMENT_KEYS.WORK_PLAN, val)
              }
              handleAttachmentView={handleAttachmentView}
              selectedType={selectedType}
              attachmentType={PERMIT_TO_WORK_ATTACHMENTS_TYPE.WORK_PLAN}
              isGetFileByTypeLoading={isGetFileByTypeLoading}
              error={errors.uploadWorkPlan}
              previewFiles={previewFiles}
              setPreviewFiles={setPreviewFiles}
              disabled={isFieldsDisabled}
              isRemovable={false}
            />

            <FileUploadInput
              name="uploadMethodStatement"
              label={t("uploadMethodStatement")}
              accept=".png,.jpg,.jpeg,.pdf"
              allowedTypes={allowedFileTypes}
              isAttachments={isAttachments?.haveMethodStatement}
              control={control}
              uploadRef={
                uploadRefs[PERMIT_TO_WORK_ATTACHMENT_KEYS.METHOD_STATEMENT]
              }
              fileName={
                fileNames[PERMIT_TO_WORK_ATTACHMENT_KEYS.METHOD_STATEMENT]
              }
              setFileName={(val) =>
                setFileName(
                  PERMIT_TO_WORK_ATTACHMENT_KEYS.METHOD_STATEMENT,
                  val,
                )
              }
              handleAttachmentView={handleAttachmentView}
              selectedType={selectedType}
              attachmentType={PERMIT_TO_WORK_ATTACHMENTS_TYPE.METHOD_STATEMENT}
              isGetFileByTypeLoading={isGetFileByTypeLoading}
              error={errors.uploadMethodStatement}
              previewFiles={previewFiles}
              setPreviewFiles={setPreviewFiles}
              disabled={isFieldsDisabled}
              isRemovable={false}
            />

            {selectedWorkCategory ===
              PERMIT_TO_WORK_CATEGORIES.HIGH_RISK_WORKS && (
              <FileUploadInput
                name="uploadRiskAssessment"
                label={t("uploadRiskAssessment")}
                accept=".png,.jpg,.jpeg,.pdf"
                allowedTypes={allowedFileTypes}
                isAttachments={isAttachments?.haveRiskAssessment}
                control={control}
                uploadRef={
                  uploadRefs[PERMIT_TO_WORK_ATTACHMENT_KEYS.RISK_ASSESSMENT]
                }
                fileName={
                  fileNames[PERMIT_TO_WORK_ATTACHMENT_KEYS.RISK_ASSESSMENT]
                }
                setFileName={(val) =>
                  setFileName(
                    PERMIT_TO_WORK_ATTACHMENT_KEYS.RISK_ASSESSMENT,
                    val,
                  )
                }
                handleAttachmentView={handleAttachmentView}
                selectedType={selectedType}
                attachmentType={PERMIT_TO_WORK_ATTACHMENTS_TYPE.RISK_ASSESSMENT}
                isGetFileByTypeLoading={isGetFileByTypeLoading}
                error={errors.uploadRiskAssessment}
                previewFiles={previewFiles}
                setPreviewFiles={setPreviewFiles}
                disabled={isFieldsDisabled}
                isRemovable={false}
              />
            )}
          </div>
        </Accordion>
      </div>

      <ViewAttachmentModal
        attachment={imageSrc}
        contentType={contentType}
        isOpen={isViewAttachmentModalOpen}
        onOpenChange={() => {
          setIsViewAttachmentModalOpen(false);
          setImageSrc("");
        }}
      />
    </>
  );
};

export default memo(Attachments);
